import { notifications } from '@mantine/notifications';
import { IconCheck } from '@tabler/icons-react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { Endpoints } from '@xver/apis';
import type {
    AttachBlockVolumeRequest,
    BlockVolume,
    CreateBlockVolumeRequest,
} from '@xver/apis/v1/block-storage/model';
import { blockStorageErrorText } from '@xver/utils/blockStorage';
import { getErrorMessage } from '@xver/utils/error';
import { useCallback } from 'react';
import { useTranslation } from 'react-i18next';

import {
    PROJECT_DETAIL_QUERY_KEY,
    PROJECT_RESOURCES_QUERY_KEY,
} from '@xver/hooks/useProjectQueries';

import { VOLUMES_QUERY_KEY } from '../../ContainerEngineInstancePage/hooks/useVolumesQuery';
import { BLOCK_VOLUMES_QUERY_KEY } from './useBlockVolumesQuery';

/** Translated message for a rejected Block storage request, for an inline `role="alert"`. */
export function useBlockStorageErrorText() {
    const { t } = useTranslation('blockStorage');
    return useCallback(
        (error: unknown) =>
            blockStorageErrorText(error, (key, values) =>
                t(key as 'errors.insufficient_credit', values)
            ) ?? getErrorMessage(error),
        [t]
    );
}

/**
 * Every Block storage write. Errors are left on each mutation for the dialog to show inline;
 * success shows a toast and refreshes every volume list.
 */
export function useBlockVolumeMutations() {
    const { t } = useTranslation('blockStorage');
    const queryClient = useQueryClient();

    const done = async (title: string, message: string) => {
        notifications.show({
            title,
            message,
            icon: <IconCheck size={18} color="#22c55e" aria-hidden="true" />,
        });
        await Promise.all([
            queryClient.invalidateQueries({ queryKey: [BLOCK_VOLUMES_QUERY_KEY] }),
            queryClient.invalidateQueries({ queryKey: [VOLUMES_QUERY_KEY] }),
            queryClient.invalidateQueries({ queryKey: PROJECT_DETAIL_QUERY_KEY }),
            queryClient.invalidateQueries({ queryKey: PROJECT_RESOURCES_QUERY_KEY }),
        ]);
    };

    const createVolume = useMutation({
        mutationFn: (data: CreateBlockVolumeRequest) =>
            Endpoints.v1.BlockStorage.createVolume(data),
        onSuccess: (volume) =>
            done(t('toast.createdTitle'), t('toast.createdMessage', { name: volume.name })),
    });

    const resizeVolume = useMutation({
        mutationFn: ({ volume, sizeGiB }: { volume: BlockVolume; sizeGiB: number }) =>
            Endpoints.v1.BlockStorage.updateVolume(volume.id, { sizeGiB }),
        onSuccess: (volume, { sizeGiB }) =>
            done(
                t('toast.resizedTitle'),
                t('toast.resizedMessage', { name: volume.name, size: sizeGiB })
            ),
    });

    const deleteVolume = useMutation({
        mutationFn: (volume: BlockVolume) => Endpoints.v1.BlockStorage.deleteVolume(volume.id),
        onSuccess: (_, volume) =>
            done(t('toast.deletedTitle'), t('toast.deletedMessage', { name: volume.name })),
    });

    const attachVolume = useMutation({
        mutationFn: ({
            volume,
            request,
        }: {
            volume: BlockVolume;
            request: AttachBlockVolumeRequest;
        }) => Endpoints.v1.BlockStorage.attachVolume(volume.id, request),
        onSuccess: (_, { volume }) =>
            done(t('toast.attachedTitle'), t('toast.attachedMessage', { name: volume.name })),
    });

    const detachVolume = useMutation({
        mutationFn: (volume: BlockVolume) => Endpoints.v1.BlockStorage.detachVolume(volume.id),
        onSuccess: (_, volume) =>
            done(t('toast.detachedTitle'), t('toast.detachedMessage', { name: volume.name })),
    });

    return { createVolume, resizeVolume, deleteVolume, attachVolume, detachVolume };
}
